import { useEffect } from 'react';
import type { Me } from './auth/authApi.ts';
import { canSeeCost, handleUnauthorized, loadMe, useAuth } from './lib/auth.ts';
import { isPublicRoute, useRoute } from './lib/router.ts';
import type { Route } from './lib/router.ts';
import { AccountMenu } from './components/AccountMenu.tsx';
import { AuthCard } from './components/AuthCard.tsx';
import { ChatView } from './views/ChatView.tsx';
import { RunView } from './views/RunView.tsx';
import { ArtifactView } from './views/ArtifactView.tsx';
import { LoginView } from './views/LoginView.tsx';
import { SetupView } from './views/SetupView.tsx';
import { AccountView } from './views/AccountView.tsx';
import { AdminRoute } from './views/AdminRoute.tsx';
import { mockArtifact } from './fixtures/mockArtifact.ts';

const FIXTURE_MODE = import.meta.env.VITE_FIXTURE === '1';

export function App(): JSX.Element {
  // A build-time constant, so the two branches never trade places between renders.
  return FIXTURE_MODE ? <FixtureApp /> : <LiveApp />;
}

/**
 * Fixture mode (spec: "the artifact viewer must render without a server") — the artifact view
 * renders the hand-written mock regardless of route, so `pnpm --filter web dev` with
 * VITE_FIXTURE=1 needs no server at all. That includes the auth server: this branch never asks
 * `me`, and never mounts anything that reads the auth state. It shows cost, as an admin would see
 * it, because the fixture exists to show every readout the view can draw.
 */
function FixtureApp(): JSX.Element {
  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand-group">
          <span className="brand">Market Analysis</span>
          <span className="brand-sub">market survey</span>
        </div>
        <span className="divider" />
        <span className="brand-sub">fixture mode</span>
      </header>
      <div className="app-body">
        <ArtifactView mode="fixture" artifact={mockArtifact} showCost />
      </div>
    </div>
  );
}

/**
 * The live app (docs/specs/accounts.md, section 8). Nothing of the shell renders until `me` has
 * answered: a person with no login must never see the app flash past on the way to the login page.
 */
function LiveApp(): JSX.Element | null {
  const route = useRoute();
  const auth = useAuth();

  useEffect(() => {
    void loadMe();
  }, []);

  useEffect(() => {
    // A signed-out person who types an app hash goes to the login page, by the same rule as a 401.
    if (auth.status === 'signed_out' && !isPublicRoute(route)) handleUnauthorized();
    // A signed-in person has no use for the login page, for example after the back button.
    if (auth.status === 'signed_in' && route.view === 'login') {
      window.location.hash = route.returnTo ?? '#/';
    }
  }, [auth.status, route]);

  // The setup page needs no login and no answer from `me`.
  if (route.view === 'setup') return <SetupView token={route.token} />;

  if (auth.status === 'loading') {
    return (
      <AuthCard>
        <span className="label auth-wait">loading…</span>
      </AuthCard>
    );
  }
  if (auth.status === 'unavailable') {
    return (
      <AuthCard title="Cannot reach the server">
        <p className="notice err" role="alert">
          {auth.message}
        </p>
        <button className="btn block" type="button" onClick={() => void loadMe()}>
          Try again
        </button>
      </AuthCard>
    );
  }
  if (auth.status === 'signed_out') {
    // Off the login route, the effect above is about to move the person there.
    return route.view === 'login' ? <LoginView returnTo={route.returnTo} /> : null;
  }
  if (route.view === 'login') return null;

  return <Shell route={route} me={auth.me} />;
}

function Shell({ route, me }: { route: Exclude<Route, { view: 'login' | 'setup' }>; me: Me }): JSX.Element {
  // The role reaches a view as the one thing the view needs from it (spec section 5).
  const showCost = canSeeCost(me.role);
  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand-group">
          <a className="brand" href="#/">
            Market Analysis
          </a>
          <span className="brand-sub">market survey</span>
        </div>
        <span className="spacer" />
        <AccountMenu me={me} />
      </header>
      <div className="app-body">
        {route.view === 'home' && <ChatView sessionId={null} />}
        {route.view === 'session' && <ChatView sessionId={route.sessionId} />}
        {route.view === 'run' && (
          <RunView sessionId={route.sessionId} runId={route.runId} showCost={showCost} />
        )}
        {route.view === 'artifact' && (
          <ArtifactView
            mode="live"
            sessionId={route.sessionId}
            runId={route.runId}
            showCost={showCost}
          />
        )}
        {route.view === 'account' && <AccountView me={me} />}
        {/* keyed by page, so that one admin page never inherits another's state */}
        {route.view === 'admin' && <AdminRoute key={route.page} page={route.page} me={me} />}
      </div>
    </div>
  );
}
